import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Eye, Pause, Pencil, Plus, Trash2, Upload, Zap } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { ThemeMiniature } from "@/components/theme/ThemeMiniature";
import { EchelleTemperature } from "@/components/commun";

const COULEUR_STATUT = { actif: "var(--success)", pause: "var(--temp-3)", brouillon: "var(--muted)" };

const Bouton = ({ icone: Icone, label, onClick, testId, primaire }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`at-btn ${primaire ? "at-btn-primary" : "at-btn-ghost"} !px-3.5 text-sm`}>
    <Icone size={15} />{label}
  </button>
);

const CarteTheme = ({ t, onAction }) => {
  const navigate = useNavigate();
  const { demarrerApercu } = useTheme();
  const apercu = () => { demarrerApercu(t); navigate("/"); };
  return (
    <div className="at-card p-4 flex flex-col gap-4" data-testid={`theme-card-${t.id}`}>
      <ThemeMiniature tokens={t.tokens} testId={`theme-miniature-${t.id}`} />
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold truncate" data-testid={`theme-name-${t.id}`}>{t.nom}</p>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold shrink-0" data-testid={`theme-status-${t.id}`}>
          <span className="w-2 h-2 at-pill" style={{ background: COULEUR_STATUT[t.statut] }} />{t.statut_label}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Bouton icone={Eye} label="Aperçu" onClick={apercu} testId={`theme-preview-${t.id}`} />
        {t.statut !== "actif" && <Bouton icone={Zap} label="Activer" primaire onClick={() => onAction(t, "activer", `Thème « ${t.nom} » activé pour tous.`)} testId={`theme-activate-${t.id}`} />}
        {t.statut === "brouillon" && <Bouton icone={Pause} label="Mettre en pause" onClick={() => onAction(t, "pause", "Thème mis en pause.")} testId={`theme-pause-${t.id}`} />}
        <Bouton icone={Copy} label="Dupliquer" onClick={() => onAction(t, "dupliquer", "Thème dupliqué en brouillon.")} testId={`theme-duplicate-${t.id}`} />
        {t.statut !== "actif" && <Bouton icone={Trash2} label="Corbeille" onClick={() => onAction(t, "corbeille", "Thème mis à la corbeille.")} testId={`theme-trash-${t.id}`} />}
        <Bouton icone={Pencil} label="Modifier" onClick={() => navigate(`/parametres/themes/${t.id}`)} testId={`theme-edit-${t.id}`} />
      </div>
    </div>
  );
};

const ImporterTheme = ({ onImporte }) => {
  const ref = useRef(null);
  const [envoi, setEnvoi] = useState(false);
  const choisir = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setEnvoi(true);
    try {
      const donnees = JSON.parse(await f.text());
      const { data } = await api.post("/themes/importer", donnees);
      toast.success(`Thème « ${data.theme.nom} » importé en brouillon.`);
      onImporte(data);
    } catch (err) {
      toast.error(err instanceof SyntaxError ? "Ce fichier n'est pas un JSON valide." : messageErreur(err));
    } finally { setEnvoi(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".json,application/json" className="hidden" onChange={choisir} data-testid="theme-import-input" />
      <button type="button" onClick={() => ref.current?.click()} disabled={envoi} className="at-btn at-btn-primary shrink-0" data-testid="theme-import-button">
        <Upload size={16} />{envoi ? "Import…" : "Importer un thème"}
      </button>
    </>
  );
};

const CreerTheme = ({ themes }) => {
  const navigate = useNavigate();
  const creer = async () => {
    const actif = themes.find((t) => t.statut === "actif");
    if (!actif) return;
    try {
      const { data } = await api.post(`/themes/${actif.id}/dupliquer`);
      await api.patch(`/themes/${data.id}`, { nom: "Nouveau thème" });
      toast.success("Nouveau thème créé en brouillon.");
      navigate(`/parametres/themes/${data.id}`);
    } catch (e) { toast.error(messageErreur(e)); }
  };
  return (
    <button type="button" onClick={creer} className="at-btn at-btn-ghost shrink-0" data-testid="theme-create-button">
      <Plus size={16} />Créer un thème
    </button>
  );
};

const RapportImport = ({ r, fermer }) => (
  <div className="at-card p-4 space-y-2 text-sm" data-testid="theme-import-report">
    <div className="flex justify-between gap-2">
      <p className="font-bold">Import de « {r.theme.nom} »</p>
      <button type="button" onClick={fermer} className="at-btn at-btn-ghost !px-3 text-sm" data-testid="theme-import-report-close">Fermer</button>
    </div>
    <p>{r.repris} réglages repris · police : {r.police}. Le thème est en brouillon : utilisez « Aperçu » pour l'essayer avant de l'activer.</p>
    {r.completes.length > 0 && <p className="at-muted">Complétés avec le thème actif : {r.completes.join(", ")}.</p>}
    {r.ignores.length > 0 && <p className="at-muted">Non utilisés par la mise en page actuelle : {r.ignores.join(", ")}.</p>}
  </div>
);

export default function Themes() {
  const { rechargerActif } = useTheme();
  const [themes, setThemes] = useState([]);
  const [rapport, setRapport] = useState(null);
  const charger = useCallback((r) => { if (r?.theme) setRapport(r); return api.get("/themes").then((res) => setThemes(res.data)); }, []);
  useEffect(() => { charger(); }, [charger]);

  const agir = async (t, verbe, message) => {
    try { await api.post(`/themes/${t.id}/${verbe}`); toast.success(message); await charger(); if (verbe === "activer") rechargerActif(); }
    catch (e) { toast.error(messageErreur(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-bold">Thèmes</h2>
          <p className="at-muted text-sm mt-1">Un seul thème actif, le même pour tous. Activer un thème met l'ancien en pause.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <CreerTheme themes={themes} />
          <ImporterTheme onImporte={charger} />
        </div>
      </div>
      {rapport && <RapportImport r={rapport} fermer={() => setRapport(null)} />}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="themes-list">
        {themes.map((t) => <CarteTheme key={t.id} t={t} onAction={agir} />)}
      </div>
      <div className="at-card p-5">
        <p className="font-bold">Températures</p>
        <p className="text-sm at-muted mt-1 mb-4">Communes à tous les thèmes et non modifiables : rouge pour une fiche qui arrive, vert pour une fiche presque gagnée.</p>
        <EchelleTemperature />
      </div>
    </div>
  );
}
